import { cs } from '../../../lib/currency';
import { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import { Input } from '../../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import {
  Download, ArrowUp, ArrowDown, ArrowUpDown, X, Search, RefreshCw,
} from 'lucide-react';

// iter342 — In-portal Cost Allocation tab. Each expense is split across
// (player, team) rows within its scope, weighted by games played (coach
// selection). Filters + per-tab CSV export.

const ALL = '__all__';
const FILTER_DEFAULTS = { age: ALL, team: ALL, search: '', minTotal: '' };

const SORT_KEYS = {
  player: (r) => (r.player || '').toLowerCase(),
  age: (r) => (r.age || '').toLowerCase(),
  team: (r) => (r.team || '').toLowerCase(),
  games_played: (r) => r.games_played ?? 0,
  tournament_shared: (r) => r.tournament_shared ?? 0,
  age_group: (r) => r.age_group ?? 0,
  team_amount: (r) => r.team_amount ?? 0,
  total: (r) => r.total ?? 0,
};

const csvEscape = (val) => {
  if (val === null || val === undefined) return '';
  const s = String(val);
  return (s.includes(',') || s.includes('"') || s.includes('\n')) ? `"${s.replace(/"/g, '""')}"` : s;
};

const SortableHeader = ({ label, sortKey, sort, setSort, className, testId }) => {
  const active = sort.key === sortKey;
  const Icon = active ? (sort.dir === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;
  const toggle = () => {
    if (!active) return setSort({ key: sortKey, dir: 'asc' });
    if (sort.dir === 'asc') return setSort({ key: sortKey, dir: 'desc' });
    return setSort({ key: null, dir: 'asc' });
  };
  return (
    <TableHead className={className}>
      <button type="button" onClick={toggle}
        className={`inline-flex items-center gap-1 select-none hover:text-blue-500 transition-colors ${active ? 'text-blue-500 dark:text-blue-400 font-semibold' : ''}`}
        data-testid={testId}>
        {label}<Icon className="h-3 w-3 opacity-70" />
      </button>
    </TableHead>
  );
};

export const CostAllocationTab = ({ tournament, rows, totals, loading, onRefresh }) => {
  const [filters, setFilters] = useState(FILTER_DEFAULTS);
  const [sort, setSort] = useState({ key: null, dir: 'asc' });
  const setFilter = (patch) => setFilters((f) => ({ ...f, ...patch }));
  const clearFilters = () => setFilters(FILTER_DEFAULTS);

  const teamNames = useMemo(() => {
    const s = new Set((rows || []).map((r) => r.team).filter(Boolean));
    return Array.from(s).sort();
  }, [rows]);

  const filtered = useMemo(() => {
    const { age, team, search, minTotal } = filters;
    const q = (search || '').trim().toLowerCase();
    const mt = minTotal === '' ? null : Number(minTotal);
    return (rows || []).filter((r) => {
      if (age !== ALL && (r.age || '').toUpperCase() !== age) return false;
      if (team !== ALL && r.team !== team) return false;
      if (mt !== null && !Number.isNaN(mt) && (r.total ?? 0) < mt) return false;
      if (q && !(r.player || '').toLowerCase().includes(q)) return false;
      return true;
    });
  }, [rows, filters]);

  const sorted = useMemo(() => {
    if (!sort.key) {
      return [...filtered].sort((a, b) =>
        (a.age || '').localeCompare(b.age || '') ||
        (a.team || '').localeCompare(b.team || '') ||
        (a.player || '').localeCompare(b.player || ''));
    }
    const getter = SORT_KEYS[sort.key];
    if (!getter) return filtered;
    const dm = sort.dir === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const av = getter(a); const bv = getter(b);
      if (av < bv) return -1 * dm;
      if (av > bv) return 1 * dm;
      return 0;
    });
  }, [filtered, sort]);

  const filteredTotal = useMemo(() => sorted.reduce((s, r) => s + (r.total || 0), 0), [sorted]);

  const chips = [];
  if (filters.age !== ALL) chips.push({ key: 'age', label: `Age: ${filters.age}`, clear: () => setFilter({ age: ALL }) });
  if (filters.team !== ALL) chips.push({ key: 'team', label: `Team: ${filters.team}`, clear: () => setFilter({ team: ALL }) });
  if (filters.minTotal !== '') chips.push({ key: 'minTotal', label: `Min $: ${filters.minTotal}`, clear: () => setFilter({ minTotal: '' }) });
  if (filters.search) chips.push({ key: 'search', label: `Search: "${filters.search}"`, clear: () => setFilter({ search: '' }) });

  const handleExportCsv = () => {
    const headers = ['Player', 'Age Group', 'Team', 'Games Played', 'Tournament-shared $', 'Age Group $', 'Team $', 'Total Allocated $'];
    const lines = [headers.join(',')];
    for (const r of sorted) {
      lines.push([
        csvEscape(r.player), csvEscape(r.age), csvEscape(r.team), r.games_played ?? 0,
        (r.tournament_shared ?? 0).toFixed(2), (r.age_group ?? 0).toFixed(2),
        (r.team_amount ?? 0).toFixed(2), (r.total ?? 0).toFixed(2),
      ].join(','));
    }
    lines.push(['', '', '', '', '', '', 'TOTAL', filteredTotal.toFixed(2)].join(','));
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const slug = (tournament?.name || 'tournament').replace(/[^a-z0-9]+/gi, '-').toLowerCase();
    a.download = `${slug}-cost-allocation-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center flex-wrap gap-2">
          <div>
            <CardTitle>Cost Allocation</CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Each expense is split by games played (coach selection). Team expense → that team; Age expense → teams in that age; Tournament expense → all games.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button size="sm" variant="ghost" onClick={onRefresh} disabled={loading} data-testid="cost-alloc-refresh-btn">
              <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />Refresh
            </Button>
            <Button size="sm" variant="outline" onClick={handleExportCsv} disabled={sorted.length === 0} data-testid="cost-alloc-export-csv-btn">
              <Download className="h-4 w-4 mr-1" />Export CSV
            </Button>
          </div>
        </div>

        {totals && (
          <div className="mt-3 flex flex-wrap gap-4 text-sm">
            <span className="text-muted-foreground">Total expenses: <strong className="text-foreground">{cs()}{(totals.total_expenses ?? 0).toFixed(2)}</strong></span>
            <span className="text-muted-foreground">Total allocated: <strong className="text-foreground">{cs()}{(totals.total_allocated ?? 0).toFixed(2)}</strong></span>
            {totals.unallocated > 0 && (
              <span className="text-amber-600 dark:text-amber-400">Unallocated (no games in scope): <strong>{cs()}{totals.unallocated.toFixed(2)}</strong></span>
            )}
          </div>
        )}

        <div className="mt-3 flex flex-wrap items-end gap-2 pt-3 border-t">
          <div className="flex flex-col gap-1">
            <Label className="text-[11px] text-muted-foreground uppercase tracking-wide">Age</Label>
            <Select value={filters.age} onValueChange={(v) => setFilter({ age: v })}>
              <SelectTrigger className="w-32 h-9" data-testid="cost-alloc-filter-age"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All ages</SelectItem>
                {(tournament?.age_categories || []).map((cat) => (
                  <SelectItem key={cat} value={cat.toUpperCase()}>{cat.toUpperCase()}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1">
            <Label className="text-[11px] text-muted-foreground uppercase tracking-wide">Team</Label>
            <Select value={filters.team} onValueChange={(v) => setFilter({ team: v })}>
              <SelectTrigger className="w-44 h-9" data-testid="cost-alloc-filter-team"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All teams</SelectItem>
                {teamNames.map((n) => (<SelectItem key={n} value={n}>{n}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1">
            <Label className="text-[11px] text-muted-foreground uppercase tracking-wide">Min total $</Label>
            <Input type="number" min="0" step="0.01" placeholder="0" value={filters.minTotal}
              onChange={(e) => setFilter({ minTotal: e.target.value })} className="w-24 h-9"
              data-testid="cost-alloc-filter-min-total" />
          </div>
          <div className="flex flex-col gap-1 grow min-w-[180px]">
            <Label className="text-[11px] text-muted-foreground uppercase tracking-wide">Search</Label>
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input type="text" placeholder="Player name…" value={filters.search}
                onChange={(e) => setFilter({ search: e.target.value })} className="pl-7 h-9"
                data-testid="cost-alloc-filter-search" />
            </div>
          </div>
        </div>

        {chips.length > 0 && (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="text-xs text-muted-foreground">Active filters:</span>
            {chips.map((chip) => (
              <button key={chip.key} type="button" onClick={chip.clear}
                className="inline-flex items-center gap-1 rounded-full bg-blue-50 border border-blue-200 text-blue-700 text-xs px-2 py-0.5 hover:bg-blue-100 transition-colors"
                data-testid={`cost-alloc-chip-${chip.key}`}>
                {chip.label}<X className="h-3 w-3" />
              </button>
            ))}
            <button type="button" onClick={clearFilters} className="text-xs text-muted-foreground underline hover:text-foreground" data-testid="cost-alloc-clear-all">Clear all</button>
          </div>
        )}
      </CardHeader>

      <CardContent>
        {(rows || []).length > 0 && (
          <div className="mb-2 text-xs text-muted-foreground flex justify-between">
            <span data-testid="cost-alloc-result-count">Showing {sorted.length} of {(rows || []).length} rows</span>
            <span className="font-medium text-foreground" data-testid="cost-alloc-filtered-total">Filtered total: {cs()}{filteredTotal.toFixed(2)}</span>
          </div>
        )}
        {sorted.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground" data-testid="cost-alloc-empty">
            {(rows || []).length === 0 ? 'No allocations yet — add expenses and select players into games' : 'No rows match the current filters'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableHeader label="Player" sortKey="player" sort={sort} setSort={setSort} testId="ca-sort-player" />
                  <SortableHeader label="Age Group" sortKey="age" sort={sort} setSort={setSort} testId="ca-sort-age" />
                  <SortableHeader label="Team" sortKey="team" sort={sort} setSort={setSort} testId="ca-sort-team" />
                  <SortableHeader label="Games Played" sortKey="games_played" sort={sort} setSort={setSort} className="text-right" testId="ca-sort-games" />
                  <SortableHeader label="Tournament $" sortKey="tournament_shared" sort={sort} setSort={setSort} className="text-right" testId="ca-sort-tour" />
                  <SortableHeader label="Age Group $" sortKey="age_group" sort={sort} setSort={setSort} className="text-right" testId="ca-sort-agedollar" />
                  <SortableHeader label="Team $" sortKey="team_amount" sort={sort} setSort={setSort} className="text-right" testId="ca-sort-teamdollar" />
                  <SortableHeader label="Total $" sortKey="total" sort={sort} setSort={setSort} className="text-right" testId="ca-sort-total" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {sorted.map((r, i) => (
                  <TableRow key={`${r.player}-${r.team}-${i}`} data-testid={`cost-alloc-row-${i}`}>
                    <TableCell className="font-medium">{r.player}</TableCell>
                    <TableCell>{r.age}</TableCell>
                    <TableCell>{r.team}</TableCell>
                    <TableCell className="text-right">{r.games_played}</TableCell>
                    <TableCell className="text-right text-muted-foreground">{cs()}{(r.tournament_shared ?? 0).toFixed(2)}</TableCell>
                    <TableCell className="text-right text-muted-foreground">{cs()}{(r.age_group ?? 0).toFixed(2)}</TableCell>
                    <TableCell className="text-right text-muted-foreground">{cs()}{(r.team_amount ?? 0).toFixed(2)}</TableCell>
                    <TableCell className="text-right font-semibold text-foreground">{cs()}{(r.total ?? 0).toFixed(2)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
